/**
 * Audit Log Filters Component
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Button } from '../../ui/button';
import { Search, Filter, X, Download } from 'lucide-react';

const EVENT_TYPES = [
  'user.login', 'user.logout', 'user.register', 'user.update',
  'content.create', 'content.update', 'content.delete',
  'admin.action', 'moderation.flag', 'moderation.review',
  'payment.create', 'payment.complete', 'withdrawal.request'
];

const ENTITY_TYPES = [
  'user', 'content', 'video', 'image', 'post',
  'comment', 'subscription', 'payment', 'withdrawal'
];

export const AuditLogFilters = ({ filters, setFilters, onExport, loading }) => {
  const clearFilters = () => {
    setFilters({
      event_type: 'all',
      entity_type: 'all',
      user_id: '',
      actor_id: '',
      success: 'all',
      date_from: '',
      date_to: '',
      search: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== 'all');

  return (
    <div className="space-y-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search logs..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className="pl-10 bg-gray-900 border-gray-700 text-white"
        />
      </div>
      
      {/* Filter Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Select
          value={filters.event_type}
          onValueChange={(value) => setFilters(prev => ({ ...prev, event_type: value }))}
        >
          <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
            <SelectValue placeholder="Event Type" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
            <SelectItem value="all">All Events</SelectItem>
            {EVENT_TYPES.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select
          value={filters.entity_type}
          onValueChange={(value) => setFilters(prev => ({ ...prev, entity_type: value }))}
        >
          <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
            <SelectValue placeholder="Entity Type" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all">All Entities</SelectItem>
            {ENTITY_TYPES.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select
          value={filters.success}
          onValueChange={(value) => setFilters(prev => ({ ...prev, success: value }))}
        >
          <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">Success</SelectItem>
            <SelectItem value="false">Failed</SelectItem>
          </SelectContent>
        </Select>
        
        <Input
          placeholder="User ID"
          value={filters.user_id}
          onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))}
          className="bg-gray-900 border-gray-700 text-white"
        />
      </div>
      
      {/* Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
          className="bg-gray-900 border-gray-700 text-white"
          placeholder="From Date"
        />
        <Input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
          className="bg-gray-900 border-gray-700 text-white"
          placeholder="To Date"
        />
        <Input
          placeholder="Actor ID"
          value={filters.actor_id}
          onChange={(e) => setFilters(prev => ({ ...prev, actor_id: e.target.value }))}
          className="bg-gray-900 border-gray-700 text-white"
        />
        
        <div className="flex gap-2">
          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={clearFilters}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onExport('csv')}
            disabled={loading}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogFilters;
